import {
  AccessEnum,
  AccessRoleEnum,
  BeneficiaryTypeEnum,
  ServiceAccessEnum,
  ServiceTypeEnum,
} from '@sr-sdks/api-sdk-axios';
import {
  Badge,
  Card,
  CardContent,
  Skeleton,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sr/ui-library';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { queryOptionsGetTitle } from '../../../../api/titles/titlesApi';
import IfHasServiceAccess from '../../../../components/IfHasServiceAccess';
import { SingleQueryResults } from '../../../../components/QueryResults/SingleQueryResults';
import { BENEFICIARY_TYPES } from '../../../../constant/Common';
import { useActiveWorkspace } from '../../../../hooks/useActiveWorkspace';
import { useSetSearchParam } from '../../../../hooks/useSetSearchParam';
import { beneficiaryService } from '../../../../reducers/services/beneficiarySlice';
import { BeneficiaryWithServiceAccessRole } from '../../../../reducers/services/beneficiaryState';
import { useAppDispatch, useTypedSelector } from '../../../../stateStore';
import { toTitleCase } from '../../../../utils/helpers/common';
import { ActivityStatementsDataTable } from './ActivityStatementsDataTable';
import { BeneficiaryAccessDataTable } from './BeneficiaryAccessDataTable';
import { MarketingAgreementsDataTable } from './MarketingAgreementsDataTable';
import { SalesReportDataTable } from './SalesReportDataTable';

interface CollectionAccountManagementSectionV3Props {
  titleId: string;
}

const CAM_TAB_PARAM = 'camTab';

/**
 * MSR3 styled "Collection Account Management" section with tabbed access to
 * Activity Statements, Marketing Agreements, Sales Reports, and Beneficiary
 * Access tables. Reuses the AppDataTable-based table components with new
 * Card-based visual layout and access control logic.
 *
 * There is deliberately no Payments tab. It listed paid invoices filtered to
 * `beneficiaryWorkspaceId` + `beneficiaryTitleId`, which is the same set the
 * Title Statements section already shows in its Disbursements and Residuals
 * tabs — the same records in two tables on one page. Per the product decision
 * those payment records live in Title Statements only, and this section covers
 * the collection account's own documents. Activity Statements is consequently
 * the first and default tab, which is also the primary document for the account.
 *
 * Every remaining tab is gated on beneficiary service access rather than a
 * workspace permission, so this section no longer reads `ViewPaymentStatements`
 * at all.
 */
const CollectionAccountManagementSectionV3 = ({
  titleId,
}: CollectionAccountManagementSectionV3Props) => {
  const dispatch = useAppDispatch();
  const [activeWorkspace] = useActiveWorkspace();
  const workspaceId = activeWorkspace?._id;

  const [isAccessLevelLoading, setIsAccessLevelLoading] = useState(true);
  const [searchParams] = useSearchParams();
  const setParam = useSetSearchParam();

  const currentBeneficiary = useTypedSelector(
    beneficiaryService.selectors.currentBeneficiary,
  );

  const titleQuery = useQuery(queryOptionsGetTitle(titleId));
  const title = titleQuery.data;

  useEffect(() => {
    setIsAccessLevelLoading(true);

    const fetchAndSetCurrentBeneficiary = async () => {
      // If beneficiary is already loaded, mark as no longer loading
      if (
        currentBeneficiary &&
        currentBeneficiary.beneficiaryTitleId === titleId &&
        currentBeneficiary.beneficiaryWorkspaceId === workspaceId
      ) {
        setIsAccessLevelLoading(false);

        return;
      }

      if (!titleId || !workspaceId) {
        setIsAccessLevelLoading(false);

        return;
      }

      try {
        const response = await dispatch(
          beneficiaryService.findAll({
            beneficiaryTitleId: titleId,
            beneficiaryWorkspaceId: workspaceId,
          }),
        );

        const isFulfilled = beneficiaryService.findAll.fulfilled.match;

        if (isFulfilled(response)) {
          const items = response.payload
            .items as BeneficiaryWithServiceAccessRole[];

          // There could be more than one (e.g. residuals), so prioritise based on beneficiaryType and service access
          const beneficiary =
            items.find((b) =>
              Object.values(BeneficiaryTypeEnum).includes(b.beneficiaryType),
            ) ?? null;

          dispatch(
            beneficiaryService.actions.setCurrentBeneficiary(beneficiary),
          );
        }
      } finally {
        setIsAccessLevelLoading(false);
      }
    };

    fetchAndSetCurrentBeneficiary();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [titleId, workspaceId, dispatch]);

  /**
   * Checks if the title has one of the Collection Account Management service types.
   *
   * The section must never render without at least one of these service types,
   * regardless of user permissions or beneficiary access.
   *
   * @returns {boolean} True if title has CollectionAccount, Disbursements, or Residuals service type
   */
  const titleHasCamServiceType = useMemo(() => {
    if (!title) return false;

    return title.serviceType.some(
      (type) =>
        type === ServiceTypeEnum.CollectionAccount ||
        type === ServiceTypeEnum.Disbursements ||
        type === ServiceTypeEnum.Residuals,
    );
  }, [title]);

  /**
   * Checks if the current beneficiary has access to view the collection account.
   *
   * Access is granted if:
   * 1. The title's service type includes CollectionAccount, Disbursements, or Residuals
   * 2. The beneficiary either:
   *    - Has ViewActivityStatements, ViewContracts, or ViewSales access
   *    - Has a Producer or CoProducer role
   *
   * Used for custom access check that depends on both permission and service access.
   *
   * @returns {boolean} True if the beneficiary has access to view collection account, false otherwise
   */
  const hasAccessToViewCollectionAccount = useMemo(() => {
    if (!currentBeneficiary || !currentBeneficiary.serviceAccessRole || !title)
      return false;

    const access = currentBeneficiary.serviceAccessRole?.access;
    const role = currentBeneficiary.serviceAccessRole?.role;
    const hasAccess =
      access[ServiceAccessEnum.ViewActivityStatements] === AccessEnum.Allow ||
      access[ServiceAccessEnum.ViewContracts] === AccessEnum.Allow ||
      access[ServiceAccessEnum.ViewSales] === AccessEnum.Allow ||
      // Redundant, since producer/co-producer do have the above access
      role === AccessRoleEnum.Producer ||
      role === AccessRoleEnum.CoProducer;

    return titleHasCamServiceType && hasAccess;
  }, [currentBeneficiary, title, titleHasCamServiceType]);

  /**
   * Per-tab visibility booleans matching the exact access logic used by
   * the IfHasServiceAccess wrappers. Used only to determine the first visible
   * tab for defaults and to validate URL-param tab selections.
   */
  const canViewActivityStatements = useMemo(() => {
    return (
      currentBeneficiary &&
      currentBeneficiary.serviceAccessRole &&
      currentBeneficiary.serviceAccessRole.access[
        ServiceAccessEnum.ViewActivityStatements
      ] === AccessEnum.Allow
    );
  }, [currentBeneficiary]);

  const canViewMarketingAgreements = useMemo(() => {
    return (
      currentBeneficiary &&
      currentBeneficiary.serviceAccessRole &&
      currentBeneficiary.serviceAccessRole.access[
        ServiceAccessEnum.ViewContracts
      ] === AccessEnum.Allow
    );
  }, [currentBeneficiary]);

  const canViewSalesReports = useMemo(() => {
    return (
      currentBeneficiary &&
      currentBeneficiary.serviceAccessRole &&
      currentBeneficiary.serviceAccessRole.access[
        ServiceAccessEnum.ViewSales
      ] === AccessEnum.Allow
    );
  }, [currentBeneficiary]);

  const canViewBeneficiaryAccess = useMemo(() => {
    const access = currentBeneficiary?.serviceAccessRole?.access;
    const role = currentBeneficiary?.serviceAccessRole?.role;

    return (
      currentBeneficiary &&
      access &&
      access[ServiceAccessEnum.ViewBeneficiaries] === AccessEnum.Allow &&
      (role === AccessRoleEnum.Producer || role === AccessRoleEnum.CoProducer)
    );
  }, [currentBeneficiary]);

  /**
   * Ordered list of tabs with visibility flags, used to determine the first
   * visible tab for defaults and to validate URL-param tab selections.
   */
  const CAM_TAB_ORDER = [
    { value: 'activity-statements', visible: canViewActivityStatements },
    { value: 'marketing-agreements', visible: canViewMarketingAgreements },
    { value: 'sales-reports', visible: canViewSalesReports },
    { value: 'beneficiary-access', visible: canViewBeneficiaryAccess },
  ];

  const firstVisibleTab =
    CAM_TAB_ORDER.find((tab) => tab.visible)?.value ?? 'activity-statements';

  /**
   * Resolve the active tab from URL param, falling back to the first visible tab
   * if the requested tab is not in the URL or is not currently visible.
   */
  const requestedTab = searchParams.get(CAM_TAB_PARAM);
  const activeTab =
    requestedTab &&
    CAM_TAB_ORDER.some((tab) => tab.value === requestedTab && tab.visible)
      ? requestedTab
      : firstVisibleTab;

  const setActiveTab = (value: string) => setParam(CAM_TAB_PARAM, value);

  if (isAccessLevelLoading) return <Skeleton className="h-64 w-full" />;

  return (
    <SingleQueryResults
      loadingFallback={<Skeleton className="h-64 w-full" />}
      query={titleQuery}
    >
      {() => {
        // Service type is the hard gate: this section must never render for titles without CAM/DAS/Residuals
        if (!titleHasCamServiceType) return null;
        // Custom visibility control to avoid showing the section when there is
        // no beneficiary access. Every remaining tab is gated on beneficiary
        // service access, so a ViewPaymentStatements permission no longer opens
        // this section on its own — that only ever unlocked the Payments tab,
        // and without it such a user would get a section with no tabs at all.
        if (!hasAccessToViewCollectionAccount) return null;

        return (
          <div className="flex flex-col gap-5">
            {/* Header Section with Title and Access Level Badges */}
            <div className="flex flex-col gap-4">
              <h2 className="text-base font-medium text-foreground">
                Collection Account Management
              </h2>
              <div className="flex flex-wrap items-center gap-2">
                <span className="whitespace-nowrap font-sans text-sm text-foreground">
                  Your access level:
                </span>
                {currentBeneficiary?.beneficiaryType && (
                  <Badge icon={null} variant="destructive">
                    {toTitleCase(currentBeneficiary.beneficiaryType)}
                  </Badge>
                )}
                <Badge icon={null} variant="destructive">
                  {toTitleCase(
                    currentBeneficiary?.party
                      ? BENEFICIARY_TYPES.PARTY
                      : BENEFICIARY_TYPES.NON_PARTY,
                  )}
                </Badge>
                <span className="whitespace-nowrap font-sans text-sm text-foreground">
                  determines the visibility of information and sections below.
                </span>
              </div>
            </div>

            {/* Card with Tabs and Table Content */}
            <Card>
              <CardContent className="p-6">
                <Tabs
                  className="flex flex-col gap-4"
                  value={activeTab}
                  onValueChange={setActiveTab}
                >
                  <TabsList className="self-start">
                    <IfHasServiceAccess
                      accessRequired={ServiceAccessEnum.ViewActivityStatements}
                    >
                      <TabsTrigger value="activity-statements">
                        Activity Statements
                      </TabsTrigger>
                    </IfHasServiceAccess>

                    <IfHasServiceAccess
                      accessRequired={ServiceAccessEnum.ViewContracts}
                    >
                      <TabsTrigger value="marketing-agreements">
                        Marketing Agreements
                      </TabsTrigger>
                    </IfHasServiceAccess>

                    <IfHasServiceAccess
                      accessRequired={ServiceAccessEnum.ViewSales}
                    >
                      <TabsTrigger value="sales-reports">
                        Sales Reports
                      </TabsTrigger>
                    </IfHasServiceAccess>

                    <IfHasServiceAccess
                      accessRequired={ServiceAccessEnum.ViewBeneficiaries}
                      roleRequired={[
                        AccessRoleEnum.Producer,
                        AccessRoleEnum.CoProducer,
                      ]}
                    >
                      <TabsTrigger value="beneficiary-access">
                        Beneficiary Access
                      </TabsTrigger>
                    </IfHasServiceAccess>
                  </TabsList>

                  <IfHasServiceAccess
                    accessRequired={ServiceAccessEnum.ViewActivityStatements}
                  >
                    <TabsContent className="m-0" value="activity-statements">
                      <ActivityStatementsDataTable />
                    </TabsContent>
                  </IfHasServiceAccess>

                  <IfHasServiceAccess
                    accessRequired={ServiceAccessEnum.ViewContracts}
                  >
                    <TabsContent className="m-0" value="marketing-agreements">
                      <MarketingAgreementsDataTable />
                    </TabsContent>
                  </IfHasServiceAccess>

                  <IfHasServiceAccess
                    accessRequired={ServiceAccessEnum.ViewSales}
                  >
                    <TabsContent className="m-0" value="sales-reports">
                      <SalesReportDataTable />
                    </TabsContent>
                  </IfHasServiceAccess>

                  <IfHasServiceAccess
                    accessRequired={ServiceAccessEnum.ViewBeneficiaries}
                    roleRequired={[
                      AccessRoleEnum.Producer,
                      AccessRoleEnum.CoProducer,
                    ]}
                  >
                    <TabsContent className="m-0" value="beneficiary-access">
                      <BeneficiaryAccessDataTable />
                    </TabsContent>
                  </IfHasServiceAccess>
                </Tabs>
              </CardContent>
            </Card>
          </div>
        );
      }}
    </SingleQueryResults>
  );
};

export { CollectionAccountManagementSectionV3 };
